import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerVentas } from '../services/ventasService';
import { obtenerGastos } from '../services/gastosService';
import { obtenerIngresosLibres } from '../services/ingresosService';
import { obtenerPorCobrar } from '../services/porCobrarService';
import { obtenerCuentas } from '../services/cuentasService';
import { obtenerPedidosDesde } from '../services/pedidosService';
import { obtenerClientes } from '../services/clientesService';
import { iconoCanal } from '../utils/canales';
import { DonaConLeyenda, BarrasH, BarrasApiladas, BarrasV } from './Graficos';
import { mensajeAmigable } from '../utils/errores';
import { formatoCantidad } from '../utils/unidades';
import SoloPlan, { usePlan } from './SoloPlan';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Periodos: cubren diario (Hoy), semanal (7 días), mensual (30 días / Este mes)
// y anual (Este año).
const PERIODOS = [
  { clave: 'hoy', etiqueta: 'Hoy' },
  { clave: '7', etiqueta: '7 días' },
  { clave: '30', etiqueta: '30 días' },
  { clave: 'mes', etiqueta: 'Este mes' },
  { clave: 'anio', etiqueta: 'Este año' },
];
function inicioDe(clave) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (clave === 'hoy') return d;
  if (clave === '7') { d.setDate(d.getDate() - 6); return d; }
  if (clave === '30') { d.setDate(d.getDate() - 29); return d; }
  if (clave === 'mes') return new Date(d.getFullYear(), d.getMonth(), 1);
  if (clave === 'anio') return new Date(d.getFullYear(), 0, 1);
  return d;
}

const Tarjeta = ({ titulo, valor, detalle, color = 'var(--color-primario,#DB2777)' }) => (
  <div className="k-card" style={{ padding: '16px' }}>
    <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>{titulo}</p>
    <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color }}>{valor}</p>
    {detalle && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF' }}>{detalle}</p>}
  </div>
);

const Seccion = ({ titulo, sub, children }) => (
  <div className="k-card" style={{ padding: '16px', marginBottom: '20px' }}>
    <p style={{ margin: sub ? '0 0 2px' : '0 0 12px', fontWeight: 'bold', color: '#374151' }}>{titulo}</p>
    {sub && <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#9CA3AF' }}>{sub}</p>}
    {children}
  </div>
);

export default function ReportesView({ productos, onVolver, onVerPlanes }) {
  // Emprende: reportes básicos. Crece en adelante: los avanzados (marcados con `av`).
  const av = usePlan().tiene('reportes_avanzados');
  const [periodo, setPeriodo] = useState('7');
  const [ventas, setVentas] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [ingresosLibres, setIngresosLibres] = useState([]);
  const [porCobrar, setPorCobrar] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [pedidos30, setPedidos30] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [error, setError] = useState(null);

  const etiquetaPeriodo = (PERIODOS.find((p) => p.clave === periodo) || {}).etiqueta || '';

  useEffect(() => {
    let activo = true;
    setVentas(null);
    setError(null);
    const desde = inicioDe(periodo).toISOString();
    const desdeFecha = desde.slice(0, 10);
    // Pedidos: se cuentan siempre hoy/7/30 (independiente del periodo elegido).
    const pedidosDesde = inicioDe('30').toISOString();
    Promise.all([
      obtenerVentas({ desde }),
      obtenerGastos({ desde: desdeFecha }),
      obtenerIngresosLibres({ desde: desdeFecha }),
      obtenerPorCobrar({ soloPendientes: true }).catch(() => []),
      obtenerCuentas().catch(() => []),
      obtenerPedidosDesde(pedidosDesde).catch(() => []),
      obtenerClientes().catch(() => []),
    ])
      .then(([v, g, il, pc, cu, pe, cl]) => {
        if (!activo) return;
        setVentas(v); setGastos(g); setIngresosLibres(il);
        setPorCobrar(pc); setCuentas(cu); setPedidos30(pe); setClientes(cl);
      })
      .catch((e) => activo && setError(mensajeAmigable(e)));
    return () => { activo = false; };
  }, [periodo]);

  const productosPorId = useMemo(() => new Map((productos || []).map((p) => [p.id, p])), [productos]);

  const r = useMemo(() => {
    if (!ventas) return null;
    const totalVendido = ventas.reduce((s, v) => s + Number(v.total || 0), 0);
    const unidades = ventas.reduce((s, v) => s + (v.cantidad || 0), 0);
    const tickets = new Set(ventas.map((v) => v.ticket_id || v.id)).size;
    const ticketPromedio = tickets > 0 ? totalVendido / tickets : 0;
    // De lo vendido, cuánto fue al crédito (fiado): ya está dentro del total.
    const fiadas = ventas.filter((v) => v.a_credito);
    const vendidoCredito = fiadas.reduce((s, v) => s + Number(v.total || 0), 0);
    const ventasCredito = new Set(fiadas.map((v) => v.ticket_id || v.id)).size;

    // Por producto (más / menos vendidos)
    const porProducto = new Map();
    ventas.forEach((v) => {
      const clave = v.producto_id || v.codigo_producto;
      const prod = productosPorId.get(v.producto_id);
      const acc = porProducto.get(clave) || { nombre: v.nombre_producto, codigo: v.codigo_producto, categoria: prod?.categoria || 'Sin categoría', cantidad: 0, total: 0, costo: 0 };
      acc.cantidad += v.cantidad || 0;
      acc.total += Number(v.total || 0);
      acc.costo += (v.cantidad || 0) * Number(v.costo_unitario || 0);
      porProducto.set(clave, acc);
    });
    const productosVendidos = [...porProducto.values()].map((p) => ({ ...p, ganancia: p.total - (p.costo || 0) }));

    // Ventas por canal (producto + ventas libres)
    const porCanal = new Map();
    ventas.forEach((v) => {
      const clave = v.canal || 'Sin especificar';
      const acc = porCanal.get(clave) || { canal: clave, cantidad: 0, total: 0 };
      acc.cantidad += v.cantidad || 0;
      acc.total += Number(v.total || 0);
      porCanal.set(clave, acc);
    });
    // Los envíos cobrados no son ventas de un canal (se le pagan al courier): van aparte en "Envíos cobrados".
    const envioDeLibre = (i) => (Number(i.envio || 0) > 0 ? Number(i.envio) : ((i.ticket_venta || i.concepto === 'Envío nacional') ? Number(i.monto || 0) : 0));
    ingresosLibres.forEach((i) => {
      const monto = Number(i.monto || 0) - envioDeLibre(i);
      if (monto <= 0) return;
      const clave = i.canal || 'Sin especificar';
      const acc = porCanal.get(clave) || { canal: clave, cantidad: 0, total: 0 };
      acc.total += monto;
      porCanal.set(clave, acc);
    });
    const canales = [...porCanal.values()].sort((a, b) => b.total - a.total);

    // Por categoría de producto: vendido, unidades, costo recuperado, ganancia.
    const porCategoria = new Map();
    ventas.forEach((v) => {
      const prod = productosPorId.get(v.producto_id);
      const cat = prod?.categoria || 'Sin categoría';
      const acc = porCategoria.get(cat) || { categoria: cat, vendido: 0, unidades: 0, costo: 0 };
      acc.vendido += Number(v.total || 0);
      acc.unidades += v.cantidad || 0;
      acc.costo += (v.cantidad || 0) * Number(v.costo_unitario || 0);
      porCategoria.set(cat, acc);
    });
    const categorias = [...porCategoria.values()]
      .map((c) => ({ ...c, ganancia: c.vendido - c.costo, margen: c.vendido > 0 ? ((c.vendido - c.costo) / c.vendido) * 100 : 0 }))
      .sort((a, b) => b.vendido - a.vendido);
    const totalCostoRecuperado = categorias.reduce((s, c) => s + c.costo, 0);
    // Categorías que tienen productos pero NO vendieron nada en el período.
    const catsConProducto = [...new Set((productos || []).map((p) => p.categoria).filter(Boolean))];
    const catsVendidasSet = new Set(categorias.map((c) => c.categoria));
    const categoriasSinVenta = catsConProducto.filter((c) => !catsVendidasSet.has(c)).sort();

    // Sin movimiento (stock > 0 y nada vendido en el periodo)
    const vendidos = new Set(ventas.map((v) => v.producto_id).filter(Boolean));
    const sinMovimiento = (productos || []).filter((p) => p.stock > 0 && !vendidos.has(p.id));
    // Capital dormido: dinero (a costo) parado en productos sin vender este período.
    const capitalDormido = sinMovimiento.reduce((s, p) => s + (p.stock || 0) * Number(p.precio_costo || 0), 0);

    // Stock valorizado
    const valorCosto = (productos || []).reduce((s, p) => s + (p.stock || 0) * Number(p.precio_costo || 0), 0);
    const valorVenta = (productos || []).reduce((s, p) => s + (p.stock || 0) * Number(p.precio_venta || 0), 0);

    // Utilidad real
    const costoVendido = ventas.reduce((s, v) => s + (v.cantidad || 0) * Number(v.costo_unitario || 0), 0);
    const totalGastos = gastos.reduce((s, g) => s + Number(g.monto || 0), 0);
    const utilidad = totalVendido - costoVendido - totalGastos;
    const margen = totalVendido > 0 ? ((totalVendido - costoVendido) / totalVendido) * 100 : 0;
    const totalOtros = ingresosLibres.reduce((s, i) => s + Number(i.monto || 0), 0);
    const costoOtros = ingresosLibres.reduce((s, i) => s + Number(i.costo || 0), 0);
    const resultadoTotal = utilidad + totalOtros - costoOtros;

    // Gastos por categoría
    const porGasto = new Map();
    gastos.forEach((g) => {
      const clave = g.categoria || 'Sin categoría';
      const acc = porGasto.get(clave) || { categoria: clave, monto: 0, num: 0 };
      acc.monto += Number(g.monto || 0);
      acc.num += 1;
      porGasto.set(clave, acc);
    });
    const gastosPorCategoria = [...porGasto.values()].sort((a, b) => b.monto - a.monto);

    // Envíos: cobrado vs pagado al courier
    const esEnvioLegacy = (i) => i.ticket_venta || i.concepto === 'Envío nacional';
    const envioDe = (i) => {
      const e = Number(i.envio || 0);
      return e > 0 ? e : (esEnvioLegacy(i) ? Number(i.monto || 0) : 0);
    };
    const enviosCobrados = ingresosLibres.reduce((s, i) => s + envioDe(i), 0);
    const numEnvios = ingresosLibres.filter((i) => envioDe(i) > 0).length;
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const esGastoEnvio = (g) => /envio|logist|transporte|domicilio/.test(norm(g.categoria));
    const enviosPagados = gastos.filter(esGastoEnvio).reduce((s, g) => s + Number(g.monto || 0), 0);
    const enviosDiferencia = enviosCobrados - enviosPagados;

    // Cuentas por cobrar (snapshot actual, no depende del periodo)
    const porCobrarTotal = (porCobrar || []).reduce((s, c) => s + (Number(c.monto_total) - Number(c.monto_pagado)), 0);
    const topDeudores = (porCobrar || [])
      .map((c) => ({ nombre: c.clientes?.nombre || 'Cliente', saldo: Number(c.monto_total) - Number(c.monto_pagado), concepto: c.concepto }))
      .sort((a, b) => b.saldo - a.saldo).slice(0, 6);

    // Deudas de tarjeta de crédito (lo "por pagar" que la app sí lleva)
    const tarjetas = (cuentas || [])
      .filter((c) => c.tipo === 'tarjeta_credito' && Number(c.saldo) < 0)
      .map((c) => ({ nombre: c.nombre, deuda: -Number(c.saldo) }));
    const deudaTarjetasTotal = tarjetas.reduce((s, c) => s + c.deuda, 0);

    // Pedidos recibidos hoy / 7 / 30 (independiente del periodo)
    const inicioHoy = inicioDe('hoy').getTime();
    const hace7 = Date.now() - 7 * 86400000;
    const hace30 = Date.now() - 30 * 86400000;
    let pedHoy = 0, pedSem = 0, pedMes = 0, pedPend = 0;
    (pedidos30 || []).forEach((p) => {
      const t = new Date(p.creado_en).getTime();
      if (t >= inicioHoy) pedHoy++;
      if (t >= hace7) pedSem++;
      if (t >= hace30) pedMes++;
      if (p.estado === 'pendiente') pedPend++;
    });

    // Mejores clientas (por total comprado en el periodo)
    const porCliente = new Map();
    ventas.forEach((v) => {
      if (!v.cliente_id) return;
      const acc = porCliente.get(v.cliente_id) || { id: v.cliente_id, total: 0, unidades: 0 };
      acc.total += Number(v.total || 0);
      acc.unidades += v.cantidad || 0;
      porCliente.set(v.cliente_id, acc);
    });
    const nombrePorCliente = new Map((clientes || []).map((c) => [c.id, c.nombre]));
    const topClientas = [...porCliente.values()]
      .map((c) => ({ ...c, nombre: nombrePorCliente.get(c.id) || 'Cliente' }))
      .sort((a, b) => b.total - a.total).slice(0, 5);

    return {
      totalVendido, unidades, tickets, ticketPromedio, vendidoCredito, ventasCredito, productosVendidos, canales, categorias, categoriasSinVenta, totalCostoRecuperado,
      sinMovimiento, capitalDormido, valorCosto, valorVenta, costoVendido, totalGastos, utilidad, margen,
      totalOtros, costoOtros, resultadoTotal, gastosPorCategoria,
      enviosCobrados, numEnvios, enviosPagados, enviosDiferencia,
      porCobrarTotal, topDeudores, tarjetas, deudaTarjetasTotal,
      pedHoy, pedSem, pedMes, pedPend, topClientas,
    };
  }, [ventas, gastos, ingresosLibres, porCobrar, cuentas, pedidos30, clientes, productos, productosPorId]);

  const th = { textAlign: 'left', padding: '8px 10px', fontSize: '11px', color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '1px solid #E5E7EB' };
  const td = { padding: '8px 10px', fontSize: '13px', color: '#111827', borderBottom: '1px solid #F3F4F6' };

  // Rankings de productos que sí aportan valor a la decisión: por GANANCIA
  // (qué reponer) y por MONTO (de dónde viene el dinero). No por unidades,
  // porque casi todo es stock 1 y darían la misma lista.
  const topGanancia = r ? r.productosVendidos.slice().sort((a, b) => b.ganancia - a.ganancia).slice(0, 10) : [];
  const topMonto = r ? r.productosVendidos.slice().sort((a, b) => b.total - a.total).slice(0, 10) : [];

  return (
    <div className="k-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 className="k-page-title">📊 Reportes</h2>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {PERIODOS.map((p) => (
            <button key={p.clave} onClick={() => setPeriodo(p.clave)}
              style={{ padding: '6px 12px', borderRadius: '999px', border: '1px solid var(--color-primario,#DB2777)', backgroundColor: periodo === p.clave ? 'var(--color-primario,#DB2777)' : 'white', color: periodo === p.clave ? 'white' : 'var(--color-primario,#DB2777)', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
              {p.etiqueta}
            </button>
          ))}
          <button onClick={onVolver} className="k-btn-soft">
            ← Volver
          </button>
        </div>
      </div>

      {!ventas && !error && (
        <div style={{ textAlign: 'center', padding: '48px', color: '#6B7280' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
          <p style={{ margin: 0, fontWeight: 600 }}>Calculando reportes...</p>
        </div>
      )}

      {error && (
        <div style={{ textAlign: 'center', padding: '32px', backgroundColor: '#FEE2E2', borderRadius: '12px', border: '1px solid #FCA5A5' }}>
          <p style={{ margin: 0, color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>
        </div>
      )}

      {r && (
        <>
          {/* Utilidad real */}
          <div style={{ marginBottom: '20px', backgroundColor: r.utilidad >= 0 ? '#ECFDF5' : '#FEF2F2', borderRadius: '12px', padding: '18px', border: `1px solid ${r.utilidad >= 0 ? '#6EE7B7' : '#FCA5A5'}` }}>
            <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#374151', fontWeight: 600 }}>💰 Utilidad real ({etiquetaPeriodo})</p>
            <p style={{ margin: '0 0 8px', fontSize: '30px', fontWeight: 'bold', color: r.utilidad >= 0 ? 'var(--k-verde)' : '#DC2626' }}>{fmt(r.utilidad)}</p>
            <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
              Ventas {fmt(r.totalVendido)} − Costo de lo vendido {fmt(r.costoVendido)} − Gastos {fmt(r.totalGastos)}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
              💡 "Ventas" incluye las ventas <strong>fiadas</strong> (se cuentan al vender, con su costo). Los abonos no se suman aparte para no contar doble.
            </p>
            {r.totalOtros > 0 && (
              <p style={{ margin: '10px 0 0', paddingTop: '10px', borderTop: '1px dashed #D1D5DB', fontSize: '13px', color: '#374151' }}>
                {r.enviosCobrados > 0 && (
                  <>
                    <strong>+ Envíos cobrados {fmt(r.enviosCobrados)}</strong>
                    <span style={{ color: '#9CA3AF' }}> (se cuadran con el pago al courier · ver 🚚 abajo)</span>
                    <br />
                  </>
                )}
                <strong>+ Otros ingresos {fmt(r.totalOtros - r.enviosCobrados)}</strong>
                <span style={{ color: '#9CA3AF' }}> (ventas libres, consultorías)</span>
                {r.costoOtros > 0 && (<><br /><span>− Costo de ventas libres {fmt(r.costoOtros)}</span></>)}
                <br />
                <span style={{ fontSize: '15px', fontWeight: 'bold', color: r.resultadoTotal >= 0 ? 'var(--k-verde)' : '#DC2626' }}>= Resultado total {fmt(r.resultadoTotal)}</span>
              </p>
            )}
          </div>

          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <Tarjeta titulo={`Vendido (${etiquetaPeriodo})`} valor={fmt(r.totalVendido)} detalle={`${r.unidades} unidades · contado + crédito`} />
            <Tarjeta titulo="💵 Vendido al contado" valor={fmt(r.totalVendido - r.vendidoCredito)} color="var(--k-verde)" detalle={`${r.tickets - r.ventasCredito} venta(s) pagada(s) al momento`} />
            <Tarjeta titulo="🧾 Vendido al crédito" valor={fmt(r.vendidoCredito)} color="#B45309" detalle={r.ventasCredito ? `${r.ventasCredito} venta(s) fiada(s) · ya incluida(s) en Vendido` : 'Sin ventas fiadas en este período'} />
            <Tarjeta titulo="Tickets / ventas" valor={String(r.tickets)} detalle={`Ticket promedio ${fmt(r.ticketPromedio)}`} color="#374151" />
            <Tarjeta titulo="Margen de ganancia" valor={`${r.margen.toFixed(1)}%`} detalle="Sobre ventas de producto" color="var(--k-verde)" />
            <Tarjeta titulo="Gastos del período" valor={fmt(r.totalGastos)} color="#DC2626" />
            <Tarjeta titulo="Otros ingresos" valor={fmt(r.totalOtros - r.enviosCobrados)} color="var(--k-verde)" detalle="Ventas libres, consultorías" />
            <Tarjeta titulo="🚚 Envíos cobrados" valor={fmt(r.enviosCobrados)} color="#0EA5E9" detalle={`${r.numEnvios} envío(s) · cuadran con el courier`} />
            <Tarjeta titulo="Stock valorizado (a costo)" valor={fmt(r.valorCosto)} color="#374151" />
            <Tarjeta titulo="Stock valorizado (a venta)" valor={fmt(r.valorVenta)} color="var(--k-verde)" detalle="Si vendieras todo" />
          </div>

          {/* Ventas por canal */}
          {av && <Seccion titulo={`📣 Ventas por canal (${etiquetaPeriodo})`}>
            <DonaConLeyenda formato={fmt} centro2="canales" vacio="Sin ventas en este período." totalLabel="Total"
              data={r.canales.map((c) => ({ label: `${iconoCanal(c.canal)} ${c.canal}`, value: c.total }))} />
          </Seccion>}

          {/* Ventas por categoría: qué categoría vende más/menos por monto */}
          <Seccion titulo={`🧩 Ventas por categoría (${etiquetaPeriodo})`}
            sub="Compara las categorías entre sí: cuál vende más y cuál menos, por monto de venta.">
            <DonaConLeyenda formato={fmt} centro2="categorías" vacio="Sin ventas en este período." totalLabel={`Total · ${r.categorias.reduce((s, c) => s + c.unidades, 0)} u`}
              data={r.categorias.map((c) => ({ label: `🏷️ ${c.categoria} · ${c.unidades} u`, value: c.vendido }))} />
            {r.categorias.length > 0 && (
              <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: r.categorias.length > 1 ? '1fr 1fr' : '1fr', gap: '10px' }}>
                <div style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '12px' }}>
                  <p style={{ margin: 0, fontSize: '12px', color: '#065F46', fontWeight: 600 }}>🏆 Categoría más vendida</p>
                  <p style={{ margin: '2px 0 0', fontSize: '16px', fontWeight: 'bold', color: 'var(--k-verde)' }}>{r.categorias[0].categoria}</p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>{fmt(r.categorias[0].vendido)} · {r.categorias[0].unidades} u</p>
                </div>
                {r.categorias.length > 1 && (
                  <div style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '10px', padding: '12px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#9A3412', fontWeight: 600 }}>🐌 Categoría menos vendida</p>
                    <p style={{ margin: '2px 0 0', fontSize: '16px', fontWeight: 'bold', color: '#EA580C' }}>{r.categorias[r.categorias.length - 1].categoria}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>{fmt(r.categorias[r.categorias.length - 1].vendido)} · {r.categorias[r.categorias.length - 1].unidades} u</p>
                  </div>
                )}
              </div>
            )}
            {r.categoriasSinVenta.length > 0 && (
              <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                🐢 <strong>Categorías sin ventas</strong> este período: {r.categoriasSinVenta.join(', ')}
              </p>
            )}
          </Seccion>

          {/* Recuperación de costo por categoría */}
          {av && <>
          <Seccion titulo={`♻️ Recuperación de costo y ganancia por categoría (${etiquetaPeriodo})`}
            sub='"Costo recuperado" = la parte de lo vendido que era costo. Es lo que puedes reinvertir para reponer esa categoría. La ganancia es lo que te queda limpio.'>
            {r.categorias.length === 0 ? (
              <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF' }}>Sin ventas en este período.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ marginBottom: '16px' }}>
                  <BarrasApiladas formato={fmt}
                    data={r.categorias.map((c) => ({ label: `🏷️ ${c.categoria}`, costo: c.costo, ganancia: c.ganancia }))} />
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
                  <thead>
                    <tr>
                      <th style={th}>Categoría</th>
                      <th style={{ ...th, textAlign: 'right' }}>Vendido</th>
                      <th style={{ ...th, textAlign: 'right' }}>Costo recuperado</th>
                      <th style={{ ...th, textAlign: 'right' }}>Ganancia</th>
                      <th style={{ ...th, textAlign: 'right' }}>Margen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.categorias.map((c) => (
                      <tr key={c.categoria}>
                        <td style={td}>🏷️ {c.categoria}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(c.vendido)}</td>
                        <td style={{ ...td, textAlign: 'right', color: '#2563EB', fontWeight: 700 }}>{fmt(c.costo)}</td>
                        <td style={{ ...td, textAlign: 'right', color: c.ganancia >= 0 ? 'var(--k-verde)' : '#DC2626', fontWeight: 700 }}>{fmt(c.ganancia)}</td>
                        <td style={{ ...td, textAlign: 'right', color: '#6B7280' }}>{c.margen.toFixed(0)}%</td>
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...td, fontWeight: 700 }}>Total</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(r.totalVendido)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#2563EB' }}>{fmt(r.totalCostoRecuperado)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--k-verde)' }}>{fmt(r.totalVendido - r.costoVendido)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#6B7280' }}>{r.margen.toFixed(0)}%</td>
                    </tr>
                  </tbody>
                </table>
                <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#2563EB', fontWeight: 600 }}>
                  💡 Para reponer lo vendido este período necesitás reinvertir ≈ {fmt(r.totalCostoRecuperado)} (repartido por categoría arriba).
                </p>
              </div>
            )}
          </Seccion>

          {/* Productos que aportan a la decisión: por ganancia y por monto */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <div className="k-card" style={{ padding: '16px' }}>
              <p style={{ margin: '0 0 2px', fontWeight: 'bold', color: '#374151' }}>💰 Los que más ganancia te dejaron</p>
              <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#9CA3AF' }}>Ganancia = precio − costo. Ideal para saber qué reponer o buscar parecido.</p>
              <BarrasH color="var(--k-verde)" formato={fmt} vacio="Sin ventas en este período."
                data={topGanancia.map((t) => ({ label: t.nombre, value: t.ganancia, extra: `vendió ${fmt(t.total)}` }))} />
            </div>

            <div className="k-card" style={{ padding: '16px' }}>
              <p style={{ margin: '0 0 2px', fontWeight: 'bold', color: '#374151' }}>🏆 Los que más venta te dieron (monto)</p>
              <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#9CA3AF' }}>De dónde viene más dinero (aunque la ganancia dependa del margen).</p>
              <BarrasH color="var(--color-primario,#DB2777)" formato={fmt} vacio="Sin ventas en este período."
                data={topMonto.map((t) => ({ label: t.nombre, value: t.total, extra: `gana ${fmt(t.ganancia)}` }))} />
            </div>
          </div>

          {/* Inventario sin movimiento + capital dormido */}
          <Seccion titulo={`🐢 Inventario sin movimiento (${r.sinMovimiento.length})`}
            sub="Productos con stock que NO se vendieron este período. El capital dormido es la plata (a costo) que tienes parada ahí — candidatos para oferta.">
            {r.sinMovimiento.length === 0 ? (
              <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF' }}>¡Todo tu inventario se está moviendo! 🎉</p>
            ) : (
              <>
                <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '10px' }}>
                  <p style={{ margin: 0, fontSize: '12px', color: '#9A3412', fontWeight: 600 }}>💤 Capital dormido (a costo)</p>
                  <p style={{ margin: '2px 0 0', fontSize: '22px', fontWeight: 'bold', color: '#EA580C' }}>{fmt(r.capitalDormido)}</p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>en {r.sinMovimiento.length} producto(s) sin venderse este período</p>
                </div>
                {r.sinMovimiento.slice(0, 12).map((p) => (
                  <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6', fontSize: '13px' }}>
                    <span style={{ color: '#111827' }}>{p.nombre} <span style={{ color: '#9CA3AF' }}>({p.codigo})</span></span>
                    <span style={{ color: '#6B7280' }}>{formatoCantidad(p.stock, p)} en stock · {fmt((p.stock || 0) * Number(p.precio_costo || 0))} a costo</span>
                  </div>
                ))}
                {r.sinMovimiento.length > 12 && (
                  <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#9CA3AF' }}>y {r.sinMovimiento.length - 12} más…</p>
                )}
              </>
            )}
          </Seccion>

          </>}

          {/* Gastos por categoría */}
          <Seccion titulo={`💸 Gastos por categoría (${etiquetaPeriodo})`}>
            <DonaConLeyenda formato={fmt} centro2="tipos" vacio="Sin gastos en este período."
              data={r.gastosPorCategoria.map((g) => ({ label: g.categoria, value: g.monto }))} />
          </Seccion>

          {/* Envíos */}
          {av && (r.enviosCobrados > 0 || r.enviosPagados > 0) && (
            <Seccion titulo={`🚚 Envíos nacionales (${etiquetaPeriodo})`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                <span style={{ color: '#374151' }}>Envíos cobrados a clientas {r.numEnvios > 0 ? `(${r.numEnvios})` : ''}</span>
                <span style={{ fontWeight: 700, color: '#0EA5E9' }}>+{fmt(r.enviosCobrados)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                <span style={{ color: '#374151' }}>Pagado al courier (gasto "Envíos")</span>
                <span style={{ fontWeight: 700, color: '#DC2626' }}>−{fmt(r.enviosPagados)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '10px 0 2px' }}>
                <span style={{ fontWeight: 700, color: '#111827' }}>
                  {r.enviosDiferencia < 0 ? 'Diferencia que absorbés' : r.enviosDiferencia > 0 ? 'Diferencia a tu favor' : 'Cuadra exacto'}
                </span>
                <span style={{ fontWeight: 'bold', color: r.enviosDiferencia < 0 ? '#DC2626' : 'var(--k-verde)' }}>
                  {r.enviosDiferencia < 0 ? '−' : r.enviosDiferencia > 0 ? '+' : ''}{fmt(Math.abs(r.enviosDiferencia))}
                </span>
              </div>
            </Seccion>
          )}

          {/* Cuentas por cobrar (snapshot actual) */}
          <Seccion titulo="🧾 Cuentas por cobrar (ahora)" sub="Lo que te deben las clientas en este momento (no depende del período).">
            <p style={{ margin: '0 0 10px', fontSize: '24px', fontWeight: 'bold', color: '#B45309' }}>{fmt(r.porCobrarTotal)}</p>
            {r.topDeudores.length === 0 ? (
              <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF' }}>🎉 ¡Nadie te debe!</p>
            ) : r.topDeudores.map((d, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #F3F4F6', fontSize: '13px' }}>
                <span style={{ color: '#111827' }}>{d.nombre} <span style={{ color: '#9CA3AF' }}>{d.concepto ? `· ${d.concepto}` : ''}</span></span>
                <span style={{ fontWeight: 'bold', color: '#B45309' }}>{fmt(d.saldo)}</span>
              </div>
            ))}
          </Seccion>

          {/* Cuentas por pagar (tarjetas de crédito) */}
          <Seccion titulo="💳 Cuentas por pagar (ahora)" sub="Lo que debés en tarjetas de crédito. El fiado a proveedores todavía no se registra en la app (módulo futuro).">
            {r.tarjetas.length === 0 ? (
              <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF' }}>No tienes deuda registrada en tarjetas de crédito. 👍</p>
            ) : (
              <>
                <p style={{ margin: '0 0 10px', fontSize: '24px', fontWeight: 'bold', color: '#DC2626' }}>{fmt(r.deudaTarjetasTotal)}</p>
                {r.tarjetas.map((t, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #F3F4F6', fontSize: '13px' }}>
                    <span style={{ color: '#111827' }}>🪙 {t.nombre}</span>
                    <span style={{ fontWeight: 'bold', color: '#DC2626' }}>{fmt(t.deuda)}</span>
                  </div>
                ))}
              </>
            )}
          </Seccion>

          {/* Pedidos recibidos */}
          <Seccion titulo="🛒 Pedidos recibidos (del catálogo)" sub="Conteo por día / semana / mes, independiente del período de arriba.">
            <BarrasV data={[
              { label: 'Hoy', value: r.pedHoy, color: '#7C3AED' },
              { label: 'Últimos 7 días', value: r.pedSem, color: '#3B82F6' },
              { label: 'Últimos 30 días', value: r.pedMes, color: '#10B981' },
            ]} />
            <p style={{ margin: '12px 0 0', fontSize: '13px', color: r.pedPend > 0 ? '#EA580C' : '#6B7280', fontWeight: 600, textAlign: 'center' }}>
              {r.pedPend > 0 ? `⏳ ${r.pedPend} pedido(s) pendiente(s) de atender` : '✅ No tienes pedidos pendientes'}
            </p>
          </Seccion>

          {/* Mejores clientas */}
          {av && <Seccion titulo={`👑 Mejores clientas (${etiquetaPeriodo})`}>
            <BarrasH color="var(--color-primario,#DB2777)" formato={fmt} vacio="Sin ventas con cliente asignado en este período. Tip: asigná la clienta al vender para ver quién compra más."
              data={r.topClientas.map((c) => ({ label: c.nombre, value: c.total, extra: `${c.unidades} u` }))} />
          </Seccion>}

          <SoloPlan funcion="reportes_avanzados" onVerPlanes={onVerPlanes}
            titulo="Reportes avanzados: ventas por canal, recuperación de costo, productos que más ganancia dejan, inventario sin movimiento, envíos y mejores clientas." />
        </>
      )}
    </div>
  );
}
